/**
 * Advance Transactions Table Component
 * Displays advance adjustment transactions - where advance was used/adjusted
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import NewFeeReciept from '../../finance/newFeeReciept';
import LoadingComponent from '../../loadingComponent';

export class AdvanceTransactionsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            flattenedData: [],
            isLoading: false,
            isEnableReceipt: false,
            receiptData: null
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind methods
        this.syncScroll = this.syncScroll.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    componentDidMount() {
        console.log('AdvanceTransactionsTable component mounted');
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }

        // Set up scroll synchronization after a short delay
        setTimeout(() => {
            this.attachScrollListener();
        }, 100);
    }

    processTransactionsData(data) {
        console.log('AdvanceTransactionsTable - Processing transactions data:', data);

        const collections = data?.collections || [];

        if (collections && collections.length > 0) {
            // Filter for advance adjustment transactions ONLY (not advance receipts)
            const advanceTransactions = [];

            collections.forEach(transaction => {
                const feePaidDetails = transaction.feePaidDetails;
                const paymentMode = transaction.paymentMode;

                // Only process if payment mode is "Adjustment"
                const isAdjustmentPayment = paymentMode && paymentMode.toLowerCase() === 'adjustment';

                if (isAdjustmentPayment && feePaidDetails && Array.isArray(feePaidDetails)) {
                    // Get all fee details (adjustments can be for any fee type)
                    const adjustmentDetails = feePaidDetails.filter(detail => detail.amountPaying);

                    if (adjustmentDetails.length > 0) {
                        const totalAmount = adjustmentDetails.reduce((sum, detail) =>
                            sum + (detail.amountPaying || 0), 0);

                        advanceTransactions.push({
                            ...transaction,
                            adjustmentDetails: adjustmentDetails,
                            total: totalAmount
                        });
                    }
                }
            });

            console.log('Filtered advance transactions:', advanceTransactions.length);

            const flattened = this.flattenTransactions(advanceTransactions);
            console.log('AdvanceTransactionsTable - Flattened Data:', flattened);

            this.setState({
                transactions: advanceTransactions,
                flattenedData: flattened,
                isLoading: false
            }, () => {
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.log('AdvanceTransactionsTable - No collections in data');
            this.setState({
                transactions: [],
                flattenedData: [],
                isLoading: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listener
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    attachScrollListener = () => {
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            // Remove existing listener first to avoid duplicates
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
            // Add new listener
            grandTotalWrapper.addEventListener('scroll', this.syncScroll);
            console.log('Scroll listener attached to grand total');
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.scrollLeft = scrollLeft;
        }
    }

    flattenTransactions = (transactions) => {
        const flattened = [];

        transactions.forEach((transaction, transIndex) => {
            const adjustmentDetails = transaction.adjustmentDetails || [];
            const totalRows = adjustmentDetails.length;

            adjustmentDetails.forEach((detail, detailIndex) => {
                flattened.push({
                    sNo: transIndex + 1,
                    transaction: transaction,
                    detail: detail,
                    transIndex: transIndex,
                    detailIndex: detailIndex,
                    isFirstRow: detailIndex === 0,
                    isLastRow: detailIndex === totalRows - 1,
                    totalRowSpan: totalRows,
                    uniqueKey: `${transaction._id}-${detailIndex}`
                });
            });
        });

        return flattened;
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    calculateGrandTotal = () => {
        const { flattenedData } = this.state;
        // Sum only the first row of each transaction (where Total is displayed)
        return flattenedData
            .filter(row => row.isFirstRow)
            .reduce((sum, row) => sum + (row.transaction.total || 0), 0);
    };

    // Column body templates
    snoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    };

    receiptNoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.transaction.receiptNo || rowData.transaction.transctionId || rowData.transaction.transactionId || rowData.transaction._id}</span>;
    };

    transactionDateBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{this.formatDate(rowData.transaction.transactionDate)}</span>;
    };

    feeTypeBodyTemplate = (rowData) => {
        const detail = rowData.detail;
        let feeTypeDisplay = detail?.feeType || 'N/A';
        if (detail?.paymentTerm && detail.paymentTerm !== 'undefined') {
            feeTypeDisplay = `${feeTypeDisplay} - ${detail.paymentTerm}`;
        }
        return <span>{feeTypeDisplay}</span>;
    };

    amountBodyTemplate = (rowData) => {
        return <span>₹{(rowData.detail?.amountPaying || rowData.detail?.amount || 0).toFixed(2)}</span>;
    };

    totalBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>₹{rowData.transaction.total?.toFixed(2) || '0.00'}</span>;
    };

    actionsBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <div className="action-buttons flex flex-column justify-content-start align-items-center gap-2">
                <Button
                    label="Print"
                    className="p-button-sm view-btn"
                    onClick={() => {
                        console.log('Print transaction:', rowData.transaction);
                        this.setState({
                            isEnableReceipt: true,
                            receiptData: rowData.transaction
                        });
                    }}
                />
            </div>
        );
    };

    rowClassName = (rowData) => {
        // No separator classes for merged rows
        return '';
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        return (
            <>
                <div className="advance-transactions-table-container">
                    {isLoading && <LoadingComponent />}

                    {!isLoading && (
                        <div className="transactions-table-wrapper">
                            <div ref={this.dataScrollRef} className="data-table-scroll-container">
                                <DataTable
                                    value={flattenedData}
                                    scrollable
                                    scrollHeight="flex"
                                    className="transactions-table"
                                    showGridlines
                                    rowClassName={this.rowClassName}
                                    emptyMessage="No advance transactions found"
                                >
                                    <Column
                                        header="S.No"
                                        body={this.snoBodyTemplate}
                                        headerClassName="sno-header"
                                        bodyClassName="sno-cell"
                                        style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}
                                    />
                                    <Column
                                        header="Receipt No"
                                        body={this.receiptNoBodyTemplate}
                                        headerClassName="receipt-no-header"
                                        bodyClassName="receipt-no-cell"
                                        style={{ width: '200px', minWidth: '200px' }}
                                    />
                                    <Column
                                        header="Transaction Date"
                                        body={this.transactionDateBodyTemplate}
                                        headerClassName="transaction-date-header"
                                        bodyClassName="transaction-date-cell"
                                        style={{ width: '150px', minWidth: '150px' }}
                                    />
                                    <Column
                                        header="Fee Type"
                                        body={this.feeTypeBodyTemplate}
                                        headerClassName="fee-type-header"
                                        bodyClassName="fee-type-cell"
                                        style={{ width: '280px', minWidth: '280px' }}
                                    />
                                    <Column
                                        header="Amount"
                                        body={this.amountBodyTemplate}
                                        headerClassName="amount-header"
                                        bodyClassName="amount-cell"
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <Column
                                        header="Total"
                                        body={this.totalBodyTemplate}
                                        headerClassName="total-header"
                                        bodyClassName="total-cell"
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={this.actionsBodyTemplate}
                                        headerClassName="actions-header"
                                        bodyClassName="actions-cell"
                                        style={{ width: '150px', minWidth: '150px', textAlign: 'center' }}
                                    />
                                </DataTable>
                            </div>

                            {/* Grand Total Row */}
                            {flattenedData.length > 0 && (
                                <div ref={this.grandTotalScrollRef}>
                                    <DataTable
                                        value={[{ total: grandTotal }]}
                                        className="advance-transactions-grand-total-table"
                                        scrollable
                                        scrollHeight="flex"
                                        showGridlines
                                    >
                                        <Column body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                        <Column body={() => ''} style={{ width: '200px', minWidth: '200px' }} />
                                        <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                        <Column body={() => <strong style={{ color: '#076EFF' }}>TOTAL :</strong>} style={{ width: '280px', minWidth: '280px', textAlign: 'left' }} />
                                        <Column body={() => <strong style={{ color: '#076EFF' }}>₹{grandTotal.toFixed(2)}</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'right' }} />
                                        <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                        <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                    </DataTable>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {this.state.isEnableReceipt && (
                    <NewFeeReciept
                        receiptData={this.state?.receiptData}
                        hideFeeReciept={() => {
                            this.setState({
                                isEnableReceipt: false,
                                receiptData: null
                            });
                        }}
                    />
                )}
            </>
        );
    }
}
